// VIP 12 - Page 3: Emergency Department
// 3-1. Patient Arrival Information
// 3-2. Underlying Disease
// 3-3. ED CPR Data
// 3-4. ED Results

import React, { useState, useEffect } from "react";

const Page3 = ({ formData, handleChange, handleCheckboxChange, dtDisplay }) => {
  // 디버깅: Page3 로드 시 formData 구조 출력
  useEffect(() => {
    console.log("=== [DEBUG] Page3 formData 구조 ===");
    console.log("전체 formData:", formData);
    console.log("Page3 관련 필드들:", {
      // Patient Arrival
      ed_arrival_datetime2: formData.ed_arrival_datetime2,
      transport_method: formData.transport_method,
      resus_room_datetime: formData.resus_room_datetime,
      // Underlying Disease
      pmhx_htn: formData.pmhx_htn,
      pmhx_dm: formData.pmhx_dm,
      pmxh_dl: formData.pmxh_dl,
      pmhx_lc: formData.pmhx_lc,
      pmhx_hf: formData.pmhx_hf,
      pmhx_stroke: formData.pmhx_stroke,
      pmhx_caod: formData.pmhx_caod,
      pmhx_pacemaker: formData.pmhx_pacemaker,
      pmhx_icd: formData.pmhx_icd,
      pmhx_ckd: formData.pmhx_ckd,
      pmhx_malig: formData.pmhx_malig,
      // ED CPR Data
      ed_cpr_attempt: formData.ed_cpr_attempt,
      ed_cpr_reason: formData.ed_cpr_reason,
      ed_compression_datetime: formData.ed_compression_datetime,
      ed_initial_rhythm: formData.ed_initial_rhythm,
      ed_intubation: formData.ed_intubation,
      ed_intubation_type: formData.ed_intubation_type,
      ed_intubation_datetime: formData.ed_intubation_datetime,
      med_acls_epi: formData.med_acls_epi,
      med_acls_epi_time: formData.med_acls_epi_time,
      med_acls_amio: formData.med_acls_amio,
      med_acls_amio_dose: formData.med_acls_amio_dose,
      defib_datetime: formData.defib_datetime,
      defib_energy: formData.defib_energy,
      defib_attempts: formData.defib_attempts,
      cpr_aline: formData.cpr_aline,
      cpr_aline_time: formData.cpr_aline_time,
      cpr_cline: formData.cpr_cline,
      cpr_cline_time: formData.cpr_cline_time,
      cpr_dis_time: formData.cpr_dis_time,
      cpr_fluid: formData.cpr_fluid,
      cpr_dis_reason: formData.cpr_dis_reason,
      cause_arrest: formData.cause_arrest,
      cause_medical: formData.cause_medical,
      cause_trauma: formData.cause_trauma,
      cause_asphyxia: formData.cause_asphyxia,
      // ED Results
      any_rosc: formData.any_rosc,
      rosc_ed: formData.rosc_ed,
      nonsust_rosc_ed: formData.nonsust_rosc_ed,
      sust_rosc_ed: formData.sust_rosc_ed,
      rosc_ecg: formData.rosc_ecg,
      ed_outcome: formData.ed_outcome,
    });

    // 현재 전송 버튼을 누르면 어떻게 전송될지 시뮬레이션
    const filteredFormData = Object.entries(formData).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value) && value.length === 0) return acc;
        if (typeof value === "string" && value.trim() === "") return acc;
        if (value === null || value === undefined) return acc;
        return { ...acc, [key]: value };
      },
      {}
    );

    console.log("Page3에서 전송될 데이터 (빈 값 제외):", filteredFormData);
    console.log("전송 API 정보:", {
      endpoint: "https://product.idea2m.com/api/vip12/save",
      method: "POST",
      payload: {
        formData: filteredFormData,
        projectId: "12",
        caseId: formData.case_id || formData.data_id,
      },
    });
    console.log("=== [DEBUG] Page3 끝 ===");
  }, [formData]);
  // 조건부 필드 표시 상태
  const [showCauseMedical, setShowCauseMedical] = useState(false);
  const [showCauseTrauma, setShowCauseTrauma] = useState(false);
  const [showCauseAsphyxia, setShowCauseAsphyxia] = useState(false);
  const [showMedAclsEpiTime, setShowMedAclsEpiTime] = useState(false);
  const [showMedAclsAmioTime, setShowMedAclsAmioTime] = useState(false);

  // 라디오 버튼 전용 핸들러
  const handleRadioClick = (e) => {
    const { name, value } = e.target;

    // 현재 선택된 값과 클릭된 값이 같으면 해제, 다르면 선택
    if (formData[name] === value) {
      handleChange({ target: { name, value: "" } });
    } else {
      handleChange({ target: { name, value } });
    }

    // 조건부 필드 표시 로직
    handleConditionalFields(name, value);
  };

  // 조건부 필드 표시 처리
  const handleConditionalFields = (name, value) => {
    switch (name) {
      case "cause_arrest":
        setShowCauseMedical(value === "medical (noncardiac)");
        setShowCauseTrauma(value === "trauma");
        setShowCauseAsphyxia(value === "asphyxial");
        break;
      case "med_acls_epi":
        setShowMedAclsEpiTime(value === "yes");
        break;
      case "med_acls_amio":
        setShowMedAclsAmioTime(value === "yes");
        break;
    }
  };

  // 재사용 가능한 라디오 필드 렌더링 함수
  const renderRadioField = (
    fieldName,
    label,
    options,
    isRequired = false,
    columnType = "2col"
  ) => {
    const wrapperClass =
      columnType === "1col" ? "input-wrapper-1column" : "input-wrapper-2column";
    const blockClass =
      columnType === "1col" ? "block-1column" : "block-2column";
    const titleClass =
      columnType === "1col" ? "item_title_1col" : "item_title_2col";
    const inputClass = columnType === "1col" ? "input_1col" : "input_2col";

    return (
      <div className={wrapperClass}>
        <div className={blockClass}>
          <label className={titleClass}>
            {label}
            {isRequired && <span className="required-mark"> *</span>}
          </label>
          <div className={inputClass}>
            <div className="rad-group">
              {options.map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={fieldName}
                    value={option.value}
                    checked={formData[fieldName] === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 텍스트 입력 필드 렌더링 함수
  const renderTextField = (
    fieldName,
    label,
    placeholder = "",
    unit = "",
    isRequired = false,
    type = "text"
  ) => {
    return (
      <div className="block-2column">
        <label className="item_title_2col">
          {label}
          {isRequired && <span className="required-mark"> *</span>}
        </label>
        <div className="input_2col">
          <input
            type={type}
            name={fieldName}
            placeholder={placeholder}
            value={formData[fieldName] || ""}
            onChange={handleChange}
          />
          {unit && <span className="unit-text">{unit}</span>}
        </div>
      </div>
    );
  };

  return (
    <section className="vip-form-section">
      <h2 className="section-subtitle">Emergency Department</h2>

      {/* 3-1. Patient Arrival Information */}
      <fieldset className="basic-info-fieldset">
        <legend>3-1. Patient Arrival Information</legend>

        <div className="input-wrapper-2column">
          {renderTextField(
            "ed_arrival_datetime2",
            "ED arrival date",
            "",
            "",
            false,
            "datetime-local"
          )}
          <div className="block-2column">
            <label className="item_title_2col">Transport method</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "public ambulance", value: "public ambulance" },
                  { label: "private ambulance", value: "private ambulance" },
                  { label: "hospital ambulance", value: "hospital ambulance" },
                  { label: "police vehicle", value: "police vehicle" },
                  { label: "taxi", value: "taxi" },
                  { label: "others", value: "others" },
                  { label: "unknown", value: "uknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="transport_method"
                      value={option.value}
                      checked={formData.transport_method === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          {renderTextField(
            "resus_room_datetime",
            "Resuscitation room admission time",
            "",
            "",
            false,
            "datetime-local"
          )}
          <div className="block-2column">
            <div className="input_2col"></div>
          </div>
        </div>
      </fieldset>

      {/* 3-2. Underlying Disease */}
      <fieldset className="basic-info-fieldset">
        <legend>3-2. Underlying Disease</legend>

        {renderRadioField(
          "pmhx_htn",
          "HTN",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}

        {renderRadioField(
          "pmhx_dm",
          "DM",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}

        {renderRadioField(
          "pmxh_dl",
          "Dyslipidemia",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}

        {renderRadioField(
          "pmhx_lc",
          "Liver Cirrhosis",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}

        {renderRadioField(
          "pmhx_hf",
          "Congestive Heart Failure",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}

        {renderRadioField(
          "pmhx_stroke",
          "Stroke",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}

        {renderRadioField(
          "pmhx_caod",
          "CAOD",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}

        {renderRadioField(
          "pmhx_pacemaker",
          "Pacemaker",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}

        {renderRadioField(
          "pmhx_icd",
          "ICD",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}

        {renderRadioField(
          "pmhx_ckd",
          "CKD",
          [
            { label: "yes (on dialysis)", value: "yes (on dialysis)" },
            { label: "yes (no dialysis)", value: "yes (no dialysis)" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}

        {renderRadioField(
          "pmhx_malig",
          "Malignancy / Hematologic Malignancy",
          [
            { label: "yes (NED state)", value: "yes (NED state)" },
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}
      </fieldset>

      {/* 3-3. ED CPR Data */}
      <fieldset className="basic-info-fieldset">
        <legend>3-3. ED CPR Data</legend>

        {renderRadioField(
          "ed_cpr_attempt",
          "ED CPR attempt",
          [
            { label: "yes", value: "yes" },
            { label: "no", value: "no" },
          ],
          false,
          "1col"
        )}

        {renderRadioField(
          "ed_cpr_reason",
          "Reasons for not attempting CPR",
          [
            { label: "ROSC at arrival", value: "ROSC at arrival" },
            {
              label: "Slim change of successful resuscitation",
              value: "Slim change of successful resuscitation",
            },
            { label: "DNR", value: "DNR" },
          ],
          false,
          "1col"
        )}

        <div className="input-wrapper-2column">
          {renderTextField(
            "ed_compression_datetime",
            "ED chest compression time",
            "",
            "",
            false,
            "datetime-local"
          )}
          <div className="block-2column">
            <label className="item_title_2col">ED initial rhythm</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "asystole", value: "asystole" },
                  { label: "PEA", value: "PEA" },
                  { label: "VF", value: "VF" },
                  { label: "pVT", value: "pVT" },
                  { label: "shockable", value: "shockable" },
                  { label: "unshockable", value: "unshockable" },
                  { label: "unchecked", value: "unchecked" },
                  { label: "unknown", value: "unknown" },
                  { label: "ROSC state", value: "ROSC state" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="ed_initial_rhythm"
                      value={option.value}
                      checked={formData.ed_initial_rhythm === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {renderRadioField(
          "ed_intubation",
          "ED intubation",
          [
            { label: "intubation", value: "intubation" },
            {
              label: "no intubation attempted",
              value: "no intubation attempted",
            },
          ],
          false,
          "1col"
        )}

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">ED intubation type</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "LMA", value: "LMA" },
                  { label: "ET", value: "ET" },
                  { label: "tracheostomy", value: "tracheostomy" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="ed_intubation_type"
                      value={option.value}
                      checked={formData.ed_intubation_type === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {renderTextField(
            "ed_intubation_datetime",
            "ED intubation date & time",
            "",
            "",
            false,
            "datetime-local"
          )}
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Epinephrine administration in ED
            </label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "yes", value: "yes" },
                  { label: "no", value: "no" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="med_acls_epi"
                      value={option.value}
                      checked={formData.med_acls_epi === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {showMedAclsEpiTime && (
            <div className="block-2column">
              {renderTextField(
                "med_acls_epi_time",
                "1st epinephrine administration time",
                "",
                "",
                false,
                "datetime-local"
              )}
            </div>
          )}
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Amiodarone administered during CPR
            </label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "yes", value: "yes" },
                  { label: "no", value: "no" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="med_acls_amio"
                      value={option.value}
                      checked={formData.med_acls_amio === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {showMedAclsAmioTime && (
            <div className="block-2column">
              {renderTextField(
                "med_acls_amio_dose",
                "Amiodarone administration time",
                "",
                "",
                false,
                "datetime-local"
              )}
            </div>
          )}
        </div>

        <div className="input-wrapper-2column">
          {renderTextField(
            "defib_datetime",
            "1st defibrillation time",
            "",
            "",
            false,
            "datetime-local"
          )}
          {renderTextField("defib_energy", "Defibrillation energy", "", "J")}
        </div>

        <div className="input-wrapper-2column">
          {renderTextField(
            "defib_attempts",
            "Number of defibrillation attempts",
            "",
            "times"
          )}
          <div className="block-2column">
            <div className="input_2col"></div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              A-line insertion during CPR
            </label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "yes", value: "yes" },
                  { label: "no", value: "no" },
                  { label: "fail", value: "fail" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="cpr_aline"
                      value={option.value}
                      checked={formData.cpr_aline === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {renderTextField(
            "cpr_aline_time",
            "A-line insertion during CPR Time",
            "",
            "",
            false,
            "datetime-local"
          )}
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              C-line insertion during CPR
            </label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "yes", value: "yes" },
                  { label: "no", value: "no" },
                  { label: "fail", value: "fail" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="cpr_cline"
                      value={option.value}
                      checked={formData.cpr_cline === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {renderTextField(
            "cpr_cline_time",
            "C-line insertion during CPR Time",
            "",
            "",
            false,
            "datetime-local"
          )}
        </div>
      </fieldset>

      {/* 3-4. ED Results */}
      <fieldset className="basic-info-fieldset">
        <legend>3-4. ED Results</legend>

        <div className="input-wrapper-2column">
          {renderTextField(
            "cpr_dis_time",
            "Time of CPR discontinuation",
            "",
            "",
            false,
            "datetime-local"
          )}
          {renderTextField(
            "cpr_fluid",
            "Amount of fluid administered during CPR",
            "",
            "cc"
          )}
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Reason for CPR discontinuation
            </label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "ROSC", value: "ROSC" },
                  { label: "Death", value: "Death" },
                  { label: "DNR", value: "DNR" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="cpr_dis_reason"
                      value={option.value}
                      checked={formData.cpr_dis_reason === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column">
            <label className="item_title_2col">Cause of arrest</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "medical (cardiac)", value: "medical (cardiac)" },
                  {
                    label: "medical (noncardiac)",
                    value: "medical (noncardiac)",
                  },
                  { label: "trauma", value: "trauma" },
                  { label: "asphyxial", value: "asphyxial" },
                  { label: "drug overdose", value: "drug overdose" },
                  { label: "drowning", value: "drowning" },
                  { label: "electrocution", value: "electrocution" },
                  {
                    label: "thermal/cold injury",
                    value: "thermal/cold injury",
                  },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="cause_arrest"
                      value={option.value}
                      checked={formData.cause_arrest === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 조건부 섹션들 */}
        {showCauseMedical && (
          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "5px",
              margin: "10px 0",
            }}
          >
            {renderRadioField(
              "cause_medical",
              "Noncardiac cause of arrest",
              [
                {
                  label: "cerebrovascular assault",
                  value: "cerebrovascular assault",
                },
                {
                  label: "acute aortic disease",
                  value: "acute aortic disease",
                },
                { label: "sepsis/septic shock", value: "sepsis/septic shock" },
                { label: "pulmonary embolism", value: "pulmonary embolism" },
                { label: "anaphylaxis", value: "anaphylaxis" },
                { label: "terminal cancer", value: "terminal cancer" },
                {
                  label: "electrolyte imbalance (hyper/hypokalemia)",
                  value: "electrolyte imbalance (hyper/hypokalemia)",
                },
                { label: "others", value: "others" },
              ],
              false,
              "1col"
            )}
          </div>
        )}

        {showCauseTrauma && (
          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "5px",
              margin: "10px 0",
            }}
          >
            {renderRadioField(
              "cause_trauma",
              "Trauma",
              [
                { label: "blunt trauma", value: "blunt trauma" },
                { label: "penetrating trauma", value: "penetratating trauma" },
                { label: "burn", value: "burn" },
              ],
              false,
              "1col"
            )}
          </div>
        )}

        {showCauseAsphyxia && (
          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "5px",
              margin: "10px 0",
            }}
          >
            {renderRadioField(
              "cause_asphyxia",
              "Asphyxia",
              [
                {
                  label: "airway obstruction by foreign body",
                  value: "airway obstruction by foreign body",
                },
                { label: "hanging", value: "hanging" },
                {
                  label: "strangulation/suffocation",
                  value: "strangulation/suffocation",
                },
              ],
              false,
              "1col"
            )}
          </div>
        )}

        {renderRadioField(
          "any_rosc",
          "ROSC regardless of location",
          [
            { label: "no", value: "no" },
            { label: "nonsustained", value: "nonsustained" },
            {
              label: "sustained (more than 20 minutes)",
              value: "sustained (more than 20 minutes)",
            },
          ],
          false,
          "1col"
        )}

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">ROSC only the ED</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "no", value: "no" },
                  { label: "nonsustained", value: "nonsustained" },
                  {
                    label: "sustained (more than 20 minutes)",
                    value: "sustained (more than 20 minutes)",
                  },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="rosc_ed"
                      value={option.value}
                      checked={formData.rosc_ed === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {renderTextField(
            "nonsust_rosc_ed",
            "Nonsustained ROSC within ED",
            "",
            "",
            false,
            "datetime-local"
          )}
        </div>

        <div className="input-wrapper-2column">
          {renderTextField(
            "sust_rosc_ed",
            "Sustained ROSC within ED",
            "",
            "",
            false,
            "datetime-local"
          )}
          <div className="block-2column">
            <label className="item_title_2col">ROSC ECG</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "STEMI", value: "STEMI" },
                  { label: "ST depression", value: "ST depression" },
                  { label: "LBBB", value: "LBBB" },
                  { label: "RBBB", value: "RBBB" },
                  {
                    label: "Nonspecific ST or T patterns",
                    value: "Nonspecific ST or T patterns",
                  },
                  {
                    label: "Normal ST segment and T wave",
                    value: "Normal ST segment and T wave",
                  },
                  { label: "Prolonged QT", value: "Prolonged QT" },
                  { label: "Others", value: "Others" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="rosc_ecg"
                      value={option.value}
                      checked={formData.rosc_ecg === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {renderRadioField(
          "ed_outcome",
          "ED Outcome",
          [
            { label: "Death", value: "Death" },
            { label: "Hospital admission", value: "Hospital admission" },
            { label: "Transfer", value: "Transfer" },
            { label: "Hopeless discharge", value: "Hopeless discharge" },
          ],
          false,
          "1col"
        )}
      </fieldset>
    </section>
  );
};

export default Page3;
